import { Component, Input } from "@angular/core";
import { forkJoin } from "rxjs";
import { SubSink } from "subsink";
import { RosterCodeSettingService } from "../services/roster-code-setting.service";
import { RosterPatternParser } from "../services/roster-pattern-parser";
import { RosterService } from "../services/roster.service";
import { IRosterCodeSetting } from "../shared/models/roster-code-setting";
import { IRosterCode } from "./models/roster-code";
import { IRosterValue } from "./models/roster/roster-value";

@Component({
  selector: "pps-roster-type-preview",
  templateUrl: "./roster-type-preview.component.html"
})
export class RosterTypePreviewComponent{
  private subs = new SubSink();
  @Input() rule: string;
  @Input() rosterCodes: IRosterCode[];
  @Input() rosterCodeSettings: IRosterCodeSetting[];

  private rosterCodesDict:{
    [code:string]: number;
  } = null;

  rosterValues: any[] = [];

  // Sample data:
  // {
  //   1_0: {foregroundColor:"#ffffff", backgroundColor:"#000000"},
  //   2_0: {foregroundColor:"#ffffff", backgroundColor:"#000000"}
  // }
  private rosterCodeSettingsObject: {
    [rosterCodeId_ticketStatusId: string]: {
      foregroundColor:string,
      backgroundColor:string
    }
  } = {};

  gridHeaders;
  gridOptions: any = {
    getRowId: param => {
      return param.data.column;
    },
    defaultColDef: {
      resizable: false,
      suppressMovable: true,
      floatingFilter: false
    },
    suppressPropertyNamesCheck: true   
  };

  constructor(
    private rosterService: RosterService,
    private _rosterCodeSettingService: RosterCodeSettingService,
    private parser: RosterPatternParser){}

  ngOnInit(){
    this.gridOptions.context = {componentParent: this};
    if (this.rosterCodeSettings){
      this.processData();
    }else{
      this.loadRosterCodes();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  processData(){
    this.rosterCodeSettings.forEach( rcs => {
      rcs.ticketStatus = rcs.ticketStatus || {id: 0, description: ""};
      this.rosterCodeSettingsObject[rcs.rosterCode.id + "_" + rcs.ticketStatus.id] = {
        foregroundColor: rcs.foregroundColor, backgroundColor: rcs.backgroundColor
      }
    });

    this.rosterCodesDict = {};
    this.rosterCodes.forEach(rc => this.rosterCodesDict[rc.code] = rc.id);
    this.parsePattern();
  }
  loadRosterCodes(){
    this.subs.sink = forkJoin([
      this.rosterService.getRosterCodes(),
      this._rosterCodeSettingService.getAll()
    ]).subscribe(
        ([rosterCodeResponse, rosterCodeSettingsResponse]) => {
          if (rosterCodeResponse.succeed){
            this.rosterCodesDict = {};
            rosterCodeResponse.result.forEach(rc => this.rosterCodesDict[rc.code] = rc.id);
            this.parsePattern();
          }else{
            console.error("failed to load roster codes: " + rosterCodeResponse.errors.join("|"));
          }
          if (rosterCodeSettingsResponse.succeed){
            this.rosterCodeSettings = rosterCodeSettingsResponse.result;
  
            this.rosterCodeSettings.forEach( rcs => {
              rcs.ticketStatus = rcs.ticketStatus || {id: 0, description: ""};
              this.rosterCodeSettingsObject[rcs.rosterCode.id + "_" + rcs.ticketStatus.id] = {
                foregroundColor: rcs.foregroundColor, backgroundColor: rcs.backgroundColor
              }
            });
          }else{
            console.error("failed to load roster settings: " + rosterCodeSettingsResponse.errors.join("|"));
          }
        },
        (error) => {
        },
        ()=>{
          console.log("Roster codes loaded");
        }
      );
  }

  parsePattern(){
    if (!this.rosterCodesDict){
      return;
    }

    let parseResult = this.parser.parse(0, new Date(), this.rule, this.rosterCodesDict);
    let rosterData = this.parser.getRosterValues(parseResult.result);
    rosterData = this.parser.setPayRollRosterCode(rosterData, this.rosterCodesDict);

    let rosterValue = {"column": "For Roster"};
    let payrollRosterCode = {"column": "For Payroll"};

    rosterData.forEach((rv, index) => {
      rosterValue["column_" + index] = rv;
      payrollRosterCode["column_" + index] = rv.prc ? (rv.prc.code === "NIGHT" ? "NS" : (rv.prc.code === "DAY" ? "DS" : rv.prc.code)) : "";
    });

    this.getColumnDefs(rosterData);

    this.rosterValues = [rosterValue, payrollRosterCode];
  }

  getColumnDefs(rosterData: IRosterValue[]){
    this.gridHeaders = [];

    this.gridHeaders.push({
      headerName: "Days",
      headerClass: this.getHeaderClass,
      field: "column",
      columnName: "column",
      cellStyle: () => { return {fontWeight: 'bold', textAlign: "center"}; },
      width: 80
    });

    rosterData.forEach((rv, index) => {
      this.gridHeaders.push({
        headerName: index + 1, //format(addDays(date, index), "d/M"),
        headerClass: this.getHeaderClass,
        rosterDay: rv,
        columnName: "column_" + index,
        field: "column_" + index + ".rosterCode.code",
        valueGetter: this.getCellValue,
        cellClass: "roster-day-value",
        cellStyle: this.getCellStyle,
        width: 35
      });
    });
  }

  private getCellStyle(params){
    let rosterValue: IRosterValue = params.colDef.rosterDay;

    // When the cell is the Group or EmployeeName
    let _this = params.context.componentParent;
    let rosterColorCodeKey = rosterValue.rc.id + "_" + rosterValue.ts.id;

    let rowIndex = params.node.rowIndex;
    if (rowIndex === 1){
      if (rosterValue.prc){
        rosterColorCodeKey = rosterValue.prc.id + "_" + 
          (_this.rosterService.hasTicketStatus(rosterValue.prc.code) ? rosterValue.ts.id : 0);
      }else{
        return {};
      }
    }

    // When the roster code has no settings
    if (!_this.rosterCodeSettingsObject.hasOwnProperty(rosterColorCodeKey)){
      return {};
    }

    let color = _this.rosterCodeSettingsObject[rosterColorCodeKey];
    if (!color){
      return {};
    }

    return {
      backgroundColor: color.backgroundColor, 
      color: color.foregroundColor
    };
  }

  private getHeaderClass(params){
    return "roster-day";
  }

  private getCellValue(params){
    var rowIndex = params.node.rowIndex;
    let _this = params.context.componentParent;
    if (rowIndex === 1){
      return _this.rosterValues[rowIndex][params.colDef.columnName];
    }

    var rosterValue: IRosterValue = params.colDef.rosterDay;
    if (rosterValue.sn){
      return rosterValue.sn;
    }

    return rosterValue.rc.code;
  }
}